// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { BrandsAndSolutionsStateModel } from "@guestsupply/App/Store/Reducers/xBrandsAndSolutions";
import {
    getHeaderLanguageWidget,
    getHeaderOrderGuideWidget,
    xIsAuthenticated,
} from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { LinkModel } from "@guestsupply/Overrides/Widgets/Header/HeaderLinkList";
import Close from "@guestsupply/XDL/Icons/Close";
import Menu from "@guestsupply/XDL/Icons/Menu";
import { buttonTransparent } from "@guestsupply/XDL/PreStyle/button";
import { color, parseBoolean, xScreen } from "@guestsupply/XDL/Utilities";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps, IconWrapper } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";

    const session = getSession(state);
    const isEducationCustomer = parseBoolean(session.properties?.isEducationCustomer);
    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        cart: getCurrentCartState(state).value,
        showSearchSectionOnMobile: (state as CustomAppState).xHeader.showSearchSectionOnMobile,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
        isEducationCustomer,
        headerOrderGuideWidget: getHeaderOrderGuideWidget(state),
        headerLanguageWidget: getHeaderLanguageWidget(state),
        isAuthenticated: xIsAuthenticated(state),
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    allowOpenParentLinks?: boolean;
    // XNG-CHANGES
    showFeaturedPartners?: boolean;
    showAllSolutions?: boolean;
    xBrandsAndSolutions?: BrandsAndSolutionsStateModel;
    // xng-change: add google event
    handleClickedLink?: (title: string, url?: string) => void;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface WidgetElementProps extends HasFields {
    showAsLink: boolean;
}
export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    // XNG-CHANGES
    headerLinkListLinkWrapper?: InjectableCss;
}

const StyledSection = getStyledWrapper("section");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "pill",
        color: "secondary",
        css: css`
            margin-right: 8px;
            padding: 6px;
            border-radius: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;

            ${IconWrapper} {
                color: ${color("text.main")};
            }

            ${xScreen("lte", "sm")} {
                width: 32px;
                height: 32px;
                padding: 6px;
            }

            ${buttonTransparent}
        `,
    },
    drawer: {
        size: 360,
        cssOverrides: {
            drawerBody: css`
                background-color: ${getColor("common.white")};
                overflow-y: hidden;
                box-shadow: none;
                ${xScreen("lte", "xs")} {
                    width: 100%;
                    transform: translate(-100%, 0);
                }
            `,
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            margin: 16px 16px 0;
            border-bottom: 1px solid ${getColor("common.border")};
            &:last-child {
                margin-bottom: 16px;
                border-bottom: initial;
            }
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css``,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "uppercase",
        color: "common.background",
    },
    punchOutUserRowText: {
        transform: "uppercase",
        color: "common.background",
    },
    mainNavigationRow: {
        css: css`
            padding: 0;
            padding-bottom: 16px;
            background-color: ${getColor("common.white")};
        `,
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        color: "text.main",
        size: 14,
        transform: "uppercase",
        weight: 500,
        lineHeight: "16px",
    },
    logoLinks: {
        typographyProps: {
            ellipsis: true,
            transform: "uppercase",
            size: 14,
            weight: 500,
            lineHeight: "16px",
        },
        css: css`
            background-color: ${getColor("common.white")};
            color: ${getColor("text.main")};
            margin: 2px;
            padding: 0;
            padding-bottom: 16px;
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    signOutRowText: {
        transform: "uppercase",
    },
    headerLinkListLinkWrapper: {
        css: css`
            ${({ isEducationCustomer }: any) =>
                !isEducationCustomer &&
                css`
                    border-top: 1px solid ${getColor("common.border")};
                    padding-top: 16px;
                `}
        `,
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer = ({
    links,
    showQuickOrder,
    quickOrderLink,
    displayVmiNavigation,
    vmiPageLinks,
    isCompactHeaderFlyout,
    showFlyoutPricingToggle,
    showFlyoutInventoryToggle,
    rootElementSelector,
    currentLocation,
    navDrawerIsOpen,
    flyoutDrawerIsOpen,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    displayPricingAndInventoryToggles,
    visibilityState,
    allowOpenParentLinks,
    isPunchOutSession,
    signOut,
    cancelPunchOut,
    // XNG-CHANGES
    showFeaturedPartners,
    showAllSolutions,
    xBrandsAndSolutions,
    headerLinkListLinkFields,
    showSearchSectionOnMobile,
    isEducationCustomer,
    headerLanguageWidget,
    headerOrderGuideWidget,
    isAuthenticated,
    // xng-change: add google event
    handleClickedLink = () => {},
}: Props) => {
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    // XNG-CHANGES
    const brands = xBrandsAndSolutions?.brandsAndSolutions?.item1;
    const solutions = xBrandsAndSolutions?.brandsAndSolutions?.item2;

    const onSignOutHandler = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const openDrawer = () => {
        isCompactHeaderFlyout ? setFlyoutDrawerIsOpen({ isOpen: true }) : setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;

    const LinkList = ({ link }: { link: LinkModel }) => {
        const { url } = useGetLink(link.fields.destination);
        if (link.fields.linkList && link.fields.linkList.length > 0) {
            const linkList = useGetLinks(link.fields.linkList, o => o.fields.destination);
            const children = linkList.map((child, index) => {
                return {
                    url: child.url,
                    title: link.fields.linkList[index].fields.title,
                    children: [],
                };
            });
            return (
                <PanelMenu
                    className="panel-menu"
                    // eslint-disable-next-line react/no-array-index-key
                    currentUrl={currentPageUrl}
                    closeOverlay={() => {
                        closeDrawer();
                        handleClickedLink(link.fields.title);
                    }}
                    title={link.fields.title}
                    panelTrigger={
                        <PanelRow className="main-navigation-row" hasChildren {...styles.mainNavigationRow}>
                            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                                {link.fields.title}
                            </Typography>
                        </PanelRow>
                    }
                    menuItems={children as any}
                    maxDepth={2}
                    layer={0}
                    {...{ url }}
                    {...styles.panelMenu}
                />
            );
        }
        return (
            <PanelRow
                className="main-navigation-row"
                {...styles.mainNavigationRow}
                // isCurrent={currentPageUrl === link.url}
                onClick={() => {
                    closeDrawer();
                    handleClickedLink(link.fields.title, url);
                }}
                onKeyPress={(event: React.KeyboardEvent) => {
                    if (event.key === " ") {
                        event.preventDefault();
                        closeDrawer();
                    }
                }}
                href={url}
                // target={headerLinkListLinkFields[index].fields.openInNewWindow ? "_blank" : ""}
            >
                <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                    {link.fields.title}
                </Typography>
            </PanelRow>
        );
    };
    // XNG-CHANGES : remove menu trigger button on mobile
    return (
        <>
            {!showSearchSectionOnMobile && (
                <Button
                    className={`menu-trigger-button ${isOpen ? "expanded" : "collapse"}`}
                    onClick={isOpen ? closeDrawer : openDrawer}
                    {...styles.menuTriggerButton}
                    data-test-selector="expandMobileMenu"
                    aria-label="mobile menu"
                >
                    <ButtonIcon className="menu-trigger-button-icon" src={Menu} size={30} />
                    <VisuallyHidden>{translate("menu")}</VisuallyHidden>
                </Button>
            )}
            <Drawer
                className="drawer"
                draggable
                position="left"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
                data-test-selector="drawerSection"
                rootElementSelector={rootElementSelector}
                headline={translate("Menu")}
            >
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {/* covers MainNavigation functionality */}
                    {!displayVmiNavigation &&
                        !isEducationCustomer &&
                        links.map((link, index) => {
                            if (
                                !!link.childrenType &&
                                link.children &&
                                link.children.filter(o => !o.excludeFromNavigation).length > 0
                            ) {
                                const href =
                                    allowOpenParentLinks && link.url && link.url !== currentPageUrl
                                        ? link.url
                                        : undefined;
                                return (
                                    <PanelMenu
                                        className="panel-menu"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        currentUrl={currentPageUrl}
                                        closeOverlay={() => {
                                            closeDrawer();
                                            handleClickedLink(link.title, currentPageUrl);
                                        }}
                                        panelTrigger={
                                            <PanelRow
                                                className="main-navigation-row"
                                                href={href}
                                                isCurrent={link.url === currentPageUrl}
                                                hasChildren={true}
                                                allowOpenParentLinks={allowOpenParentLinks}
                                                {...styles.mainNavigationRow}
                                            >
                                                <Typography
                                                    className="main-navigation-row-text"
                                                    {...styles.mainNavigationRowTypography}
                                                >
                                                    {link.title}
                                                </Typography>
                                            </PanelRow>
                                        }
                                        menuItems={link.children}
                                        maxDepth={link.maxDepth || 3}
                                        layer={0}
                                        allowOpenParentLinks={allowOpenParentLinks}
                                        {...styles.panelMenu}
                                    />
                                );
                            }
                            return (
                                <PanelRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    className="main-navigation-row"
                                    onClick={() => {
                                        closeDrawer();
                                        handleClickedLink(link.title, link.url);
                                    }}
                                    {...styles.mainNavigationRow}
                                >
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {link.title}
                                    </Typography>
                                </PanelRow>
                            );
                        })}
                    {/* XNG-CHANGES: Adding Brands and solutions to drawer navigation */}
                    {!displayVmiNavigation && showFeaturedPartners && brands && !isEducationCustomer && (
                        <PanelMenu
                            className="panel-menu"
                            // eslint-disable-next-line react/no-array-index-key
                            key="brand"
                            currentUrl={currentPageUrl}
                            closeOverlay={() => {
                                closeDrawer();
                                handleClickedLink(brands.brandPageTitle, currentPageUrl);
                            }}
                            panelTrigger={
                                <PanelRow className="main-navigation-row" hasChildren {...styles.mainNavigationRow}>
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {brands.brandPageTitle}
                                    </Typography>
                                </PanelRow>
                            }
                            menuItems={brands.headerFeaturedBrands as any}
                            maxDepth={2}
                            layer={0}
                            title={translate("All Brands")}
                            // XNG-CHANGES : GUEST-2701 : Removing marketing section from mobile view
                            {...{ url: brands.brandPageUrl }}
                            {...styles.panelMenu}
                        />
                    )}
                    {!displayVmiNavigation && showAllSolutions && solutions && !isEducationCustomer && (
                        <PanelMenu
                            className="panel-menu"
                            // eslint-disable-next-line react/no-array-index-key
                            key="solution"
                            currentUrl={currentPageUrl}
                            closeOverlay={() => {
                                closeDrawer();
                                handleClickedLink(
                                    solutions?.solutionPageTitle ?? translate("Our Solutions"),
                                    currentPageUrl,
                                );
                            }}
                            panelTrigger={
                                <PanelRow className="main-navigation-row" hasChildren {...styles.mainNavigationRow}>
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {solutions?.solutionPageTitle ?? translate("Our Solutions")}
                                    </Typography>
                                </PanelRow>
                            }
                            menuItems={solutions.headerSolutions as any}
                            maxDepth={2}
                            layer={0}
                            title={translate("All Solutions")}
                            // XNG-CHANGES : GUEST-2701 : Removing marketing section from mobile view
                            {...{ url: solutions.solutionPageUrl }}
                            {...styles.panelMenu}
                        />
                    )}
                    <StyledWrapper {...styles.headerLinkListLinkWrapper} {...{ isEducationCustomer }}>
                        {/* XNG-CHANGES: HeaderLinkList functionality  */}
                        {!displayVmiNavigation &&
                            headerLinkListLinkFields?.length > 0 &&
                            headerLinkListLinkFields?.map((link, index) => {
                                return <LinkList link={link} key={index} />;
                            })}
                        {!displayVmiNavigation && showQuickOrder && quickOrderLink && (
                            <PanelRow
                                className="main-navigation-row"
                                isCurrent={currentPageUrl === quickOrderLink.url}
                                onClick={() => {
                                    closeDrawer();
                                    handleClickedLink(quickOrderLink.title, quickOrderLink.url);
                                }}
                                href={quickOrderLink.url}
                                {...styles.mainNavigationRow}
                            >
                                <Typography
                                    className="main-navigation-row-text"
                                    {...styles.mainNavigationRowTypography}
                                >
                                    {quickOrderLink.title}
                                </Typography>
                            </PanelRow>
                        )}

                        <PanelRow className="main-navigation-row" onClick={closeDrawer} {...styles.mainNavigationRow}>
                            {createWidgetElement("Header/OrderGuide", {
                                fields: {
                                    ...headerOrderGuideWidget,
                                },
                                showAsLink: true,
                            } as WidgetElementProps)}
                        </PanelRow>
                        <PanelRow className="main-navigation-row" onClick={closeDrawer} {...styles.mainNavigationRow}>
                            {createWidgetElement("Header/Language", {
                                fields: {
                                    ...headerLanguageWidget,
                                },
                                showAsLink: true,
                            } as WidgetElementProps)}
                        </PanelRow>
                        {isAuthenticated && (
                            <PanelRow
                                className="main-navigation-row"
                                onClick={() => {
                                    onSignOutHandler();
                                    handleClickedLink(
                                        isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out"),
                                    );
                                }}
                                {...styles.mainNavigationRow}
                            >
                                <Typography className="sign-out-row-text" {...styles.signOutRowText}>
                                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                                </Typography>
                            </PanelRow>
                        )}
                    </StyledWrapper>

                    {displayVmiNavigation &&
                        !isEducationCustomer &&
                        vmiPageLinks &&
                        vmiPageLinks.map(
                            link =>
                                link && (
                                    <PanelRow
                                        className="main-navigation-row"
                                        key={link.id}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={() => {
                                            closeDrawer();
                                            handleClickedLink(link.title, link.url);
                                        }}
                                        href={link.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ),
                        )}
                    {displayPricingAndInventoryToggles && (
                        <>
                            {(!isCompactHeaderFlyout || showFlyoutInventoryToggle) && (
                                <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                            )}
                            {(!isCompactHeaderFlyout || showFlyoutPricingToggle) && (
                                <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                            )}
                        </>
                    )}
                </StyledSection>
            </Drawer>
        </>
    );
};

function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
